import {
  PrimitiveValueType,
  RowDataObject,
  RowDataReducerState,
  RowDataReducerAction,
  ErrorState,
} from "../../../../../types";
import { noError } from "../../../validations";

const FIELD_KEYS = {
  MINIMUM: "minimum",
  MAXIMUM: "maximum",
  TYPE: "type",
} as const;

const ERROR_MESSAGES = {
  INVALID_PERCENTAGE_VALUE: "Percentage value cannot exceed 100.",
  MAXIMUM_LESS_THAN_MINIMUM:
    "Maximum Price must be greater than Minimum Price.",
  OVERLAPPING_RANGES: "Price escalator ranges cannot overlap.",
} as const;

type NumericRange = {
  min: number;
  max: number;
};

const isValidRange = (row: RowDataObject): NumericRange | null => {
  const { minimum, maximum } = row;
  const isValid =
    minimum != null &&
    minimum !== "" &&
    maximum != null &&
    maximum !== "" &&
    Number.isFinite(Number(minimum)) &&
    Number.isFinite(Number(maximum));

  return isValid
    ? { min: Number(row.minimum), max: Number(row.maximum) }
    : null;
};

const validateMinMaxValue = (row: RowDataObject): ErrorState => {
  const range = isValidRange(row);

  if (range && range.min >= range.max) {
    return {
      error: true,
      message: ERROR_MESSAGES.MAXIMUM_LESS_THAN_MINIMUM,
    };
  }

  return noError();
};

const getOverlappingRows = (rows: RowDataObject[]): Set<number> => {
  const validRanges = rows
    .map((row, index) => {
      const range = isValidRange(row);
      return range && range.min < range.max ? { index, ...range } : null;
    })
    .filter((range) => range !== null)
    .sort((a, b) => a.min - b.min);

  const overlappingRows = new Set<number>();

  for (let i = 0; i < validRanges.length; i++) {
    const current = validRanges[i];

    for (let j = i + 1; j < validRanges.length; j++) {
      const next = validRanges[j];

      // Sorted by minimum, so later ranges cannot overlap after this point.
      if (next.min > current.max) {
        break;
      }

      overlappingRows.add(current.index);
      overlappingRows.add(next.index);
    }
  }

  return overlappingRows;
};

const getRangeOverlapValidation = (): ErrorState => ({
  error: true,
  message: ERROR_MESSAGES.OVERLAPPING_RANGES,
});

const getRowRangeValidation = (
  row: RowDataObject,
  index: number,
  overlappingRows: Set<number>,
): ErrorState => {
  const minMaxValidation = validateMinMaxValue(row);

  if (minMaxValidation.error) {
    return minMaxValidation;
  }

  return overlappingRows.has(index) ? getRangeOverlapValidation() : noError();
};

const dispatchRangeValidation = (
  propertyKey: string,
  rowIndex: number,
  validation: ErrorState,
  dispatch: React.Dispatch<RowDataReducerAction>,
): void => {
  for (const propertySubKey of [FIELD_KEYS.MINIMUM, FIELD_KEYS.MAXIMUM]) {
    dispatch({
      type: "UPDATE_FIELD_VALIDATION",
      payload: {
        propertyKey,
        propertySubKey,
        rowIndex,
        newFieldValueValidation: validation,
      },
    });
  }
};

export const checkPriceBreakEscalatorRangeOverlap = (
  propertyKey: string,
  rowIndex: number | undefined,
  rowDataReducerState: RowDataReducerState,
  dispatch: React.Dispatch<RowDataReducerAction>,
): ErrorState => {
  const rows = (rowDataReducerState.rowData[propertyKey] ??
    []) as RowDataObject[];

  const overlappingRows = getOverlappingRows(rows);

  rows.forEach((row, index) => {
    const validation = getRowRangeValidation(row, index, overlappingRows);

    dispatchRangeValidation(propertyKey, index, validation, dispatch);
  });

  if (rowIndex === undefined || !rows[rowIndex]) {
    return noError();
  }

  return getRowRangeValidation(rows[rowIndex], rowIndex, overlappingRows);
};

const validateValue = (
  value: number,
  currentType: unknown,
  currentRow: RowDataObject,
): ErrorState => {
  if (currentType === "PERCENT" && value > 100) {
    return {
      error: true,
      message: ERROR_MESSAGES.INVALID_PERCENTAGE_VALUE,
    };
  }

  const range = currentType === "AMOUNT" ? isValidRange(currentRow) : null;

  return range && (value < range.min || value > range.max)
    ? {
        error: true,
        message: `Value must be between ${range.min} and ${range.max}.`,
      }
    : noError();
};

export const checkPriceBreakEscalatorValue = (
  propertyKey: string,
  propertySubKey: string | undefined,
  rowIndex: number | undefined,
  inputValue: PrimitiveValueType,
  rowDataReducerState: RowDataReducerState,
  dispatch: React.Dispatch<RowDataReducerAction>,
): ErrorState => {
  if (
    rowIndex === undefined ||
    propertySubKey === undefined ||
    inputValue === undefined
  ) {
    return noError();
  }

  const rows = (rowDataReducerState.rowData[propertyKey] ??
    []) as RowDataObject[];

  const currentRow = rows[rowIndex];

  if (currentRow?.[propertySubKey] === undefined) {
    return noError();
  }

  const validation = validateValue(
    Number(inputValue),
    currentRow[FIELD_KEYS.TYPE],
    currentRow,
  );

  dispatch({
    type: "UPDATE_FIELD_VALIDATION",
    payload: {
      propertyKey,
      propertySubKey,
      rowIndex,
      newFieldValueValidation: validation,
    },
  });

  return validation;
};
